import {
  EmployeesReportColumn,
  EmployeesReportView,
  EnrollmentStatus,
} from '@zorro/clients';
import { Title } from '@zorro/design-system/v2/Title';
import { getToday, parseDateISO } from '@zorro/shared/formatters';
import { Grid, Space, Stack, Text } from '@zorro/web/design-system';
import {
  useAppRouter,
  useHasAllowanceModel,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';

import { CompanyQuickActionsOverview } from '../CompanyQuickActionsOverview';
import { NeedsHelp } from '../NeedsHelp';
import { CompanyCoverageOverview } from './CompanyCoverageOverview';
import CompanyEnrollmentOverview from './CompanyEnrollmentOverview';
import { CompanyOverviewSkeleton } from './CompanyOverviewSkeleton';

interface CompanySummaryProps {
  employerId: string;
}

export const CompanyOverview = ({ employerId }: CompanySummaryProps) => {
  const { isEmployerAdmin } = useRoles();

  const { data: companyOverviewReport, isLoading } = useMonolithQuery({
    method: 'employerReportsControllerGenerateCompanyOverviewReport',
    params: [employerId],
  });
  const { navigateToRoute } = useAppRouter();

  const {
    data: openEnrollmentPeriods = [],
    isLoading: isOnboardingPeriodsLoading,
  } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const { hasAllowanceModel } = useHasAllowanceModel(
    openEnrollmentPeriods,
    employerId
  );
  if (isLoading || isOnboardingPeriodsLoading) {
    return <CompanyOverviewSkeleton />;
  }

  const formattedToday = getToday().format('dddd, MMMM D');
  const onClick = (
    status: EnrollmentStatus,
    year?: number,
    params?: Record<string, string>
  ) => {
    const openEnrollmentPeriodId = openEnrollmentPeriods.find(
      (period) => parseDateISO(period.effectiveFrom).year() === year
    )?.id;

    const queryParams = {
      enrollmentPeriodId: openEnrollmentPeriodId || '',
      filters: JSON.stringify([
        {
          type: EmployeesReportColumn.enrollmentStatus,
          value: [status],
        },
      ]),
      ...params,
    };

    return isEmployerAdmin
      ? navigateToRoute('DON_EMPLOYEES', [{}], queryParams)
      : navigateToRoute(
          'OPS_EMPLOYERS_EMPLOYEES',
          [
            {
              employerId,
            },
          ],
          queryParams
        );
  };

  return (
    <Stack gap="xxs">
      {companyOverviewReport && (
        <>
          <Title>{companyOverviewReport.companyName} overview</Title>
          <Text c="zorroGray.4">{formattedToday}</Text>
          <Space h="2rem" />
          <CompanyCoverageOverview
            eligibleEmployeesCount={
              companyOverviewReport.eligibleEmployeesCount
            }
            currentCoverageCountByStatus={
              companyOverviewReport.currentCoverageCountByStatus
            }
            enrollmentYear={companyOverviewReport.currentYear}
            onClick={onClick}
          />
          {companyOverviewReport.openEnrollmentYear && (
            <>
              <Space h="2rem" />
              <CompanyEnrollmentOverview
                title="open enrollment"
                enrollmentYear={companyOverviewReport.openEnrollmentYear}
                enrollmentCounts={
                  companyOverviewReport.openEnrollmentCountByStatus
                }
                onClick={onClick}
                params={{ view: EmployeesReportView.OPEN_ENROLLMENT }}
              />
            </>
          )}
          {companyOverviewReport.currentYear && (
            <>
              <Space h="2rem" />
              <CompanyEnrollmentOverview
                title="enrollment activities"
                enrollmentYear={companyOverviewReport.currentYear}
                enrollmentCounts={
                  companyOverviewReport.currentYearEnrollmentCountByStatus
                }
                onClick={onClick}
                params={{ view: EmployeesReportView.ENROLLMENTS_IN_PROCESS }}
              />
            </>
          )}
        </>
      )}
      <Grid pb="xl" mt="var(--space-32)">
        <Grid.Col span={hasAllowanceModel ? 8 : 12}>
          <Stack>
            <CompanyQuickActionsOverview
              employerId={employerId}
              isDon={isEmployerAdmin}
              hasAllowanceModel={hasAllowanceModel}
            />
          </Stack>
        </Grid.Col>
        {isEmployerAdmin && hasAllowanceModel && (
          <Grid.Col span={4} pl="md">
            <NeedsHelp />
          </Grid.Col>
        )}
      </Grid>
    </Stack>
  );
};
